import { Info } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
      {title}
    </h4>
    <div className="text-sm leading-relaxed text-zinc-400">{children}</div>
  </div>
);

const TabRow = ({ name, body }) => (
  <div className="flex gap-3">
    <span className="w-28 shrink-0 text-sm font-semibold text-zinc-200">
      {name}
    </span>
    <span className="text-sm text-zinc-400">{body}</span>
  </div>
);

/**
 * Collapsible explainer for the project register, so a new reader doesn't
 * need to be told out-of-band what "combined score" or "SLA breach" means.
 * Lives as a small "i" icon in the page header; clicking it drops the
 * explainer down from the top-right corner instead of taking up a full-width
 * bar in the page body.
 */
const HowThisWorksPanel = () => {
  return (
    <div className="group relative">
      <button
        type="button"
        aria-label="How this works"
        className="flex items-center gap-1.5 rounded-xl border border-zinc-700/80 bg-zinc-800/80 px-3.5 py-2 text-xs font-medium text-zinc-300 shadow-sm transition-all hover:border-zinc-600 hover:bg-zinc-700/80 hover:text-zinc-100"
      >
        <Info className="h-3.5 w-3.5 text-indigo-400" />
        <span>How this works</span>
      </button>

      <div className="bg-zinc-900/98 invisible absolute right-0 top-11 z-50 flex max-h-[75vh] w-[420px] flex-col gap-4 overflow-y-auto rounded-2xl border border-zinc-800 p-5 opacity-0 shadow-2xl backdrop-blur-2xl transition-all duration-150 group-hover:visible group-hover:opacity-100">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-zinc-100">
            How this works
          </span>
          <span className="shrink-0 whitespace-nowrap rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold text-indigo-300">
            Data Guard Verified
          </span>
        </div>

        <Section title="Where this data comes from">
          Nothing here is entered by hand. Every row is extracted from a meeting
          transcript by the pipeline: a promise becomes a{' '}
          <span className="text-zinc-300">commitment</span>, a ruling becomes a{' '}
          <span className="text-zinc-300">decision</span>, and a worry, blocker,
          or open dependency becomes a{' '}
          <span className="text-zinc-300">concern</span> (subtype RISK,
          DEPENDENCY, or BLOCKER). Each is validated against the transcript
          before it lands here — a quote that doesn't match its segment is
          rejected, not smoothed over.
        </Section>

        <Section title="The five tabs">
          <div className="flex flex-col gap-2">
            <TabRow
              name="Commitments"
              body="Promises made in meetings, with a computed likelihood (firmness + speaker's track record + evidence quality) and a stall flag if repeated without ever completing."
            />
            <TabRow
              name="Approvals"
              body="Decisions and rulings made in meetings — scope calls, technical choices, process changes — with who decided and why."
            />
            <TabRow
              name="Dependencies"
              body="Dependencies and blockers. Admitted on their own evidence, not scored or ranked against risks — a live blocker doesn't compete with a speculative risk for attention."
            />
            <TabRow
              name="Risks"
              body="Scored concerns: impact (criticality/schedule/visibility/revenue) × likelihood (certainty/calibration/corroboration). Only the top 5 per project make the register; a hypothesis needs independent corroboration before it's scored at all."
            />
            <TabRow
              name="SLA"
              body="Concerns with a response-window deadline, split into Breached and Upcoming by that date."
            />
          </div>
        </Section>

        <Section title="What the numbers mean">
          <ul className="list-disc space-y-1 pl-4">
            <li>
              <span className="text-zinc-300">Criticality band</span>{' '}
              (LOW/MEDIUM/HIGH/CRITICAL) — how bad this is if it happens.
            </li>
            <li>
              <span className="text-zinc-300">Combined score</span> — band
              multiplier × likelihood, on a 0–4 scale. A near-certain trivial
              risk and a barely-possible catastrophe both score low; only a
              likely, high-impact risk scores high.
            </li>
            <li>
              <span className="text-zinc-300">SLA breach date</span> — when this
              concern was raised, plus its response window (5 days by default,
              or a stated deadline).
            </li>
          </ul>
        </Section>

        <Section title="The action buttons">
          Escalate, Mark resolved, Mark materialised, and Dismiss all record a
          decision — an entry in the concern's history — and move it between
          Open and Recently closed. Nothing is deleted; reprocessing a meeting
          never erases a prior record, so the trail stays intact even as a
          concern's status changes.
        </Section>
      </div>
    </div>
  );
};

export default HowThisWorksPanel;
